/**
 * Unified RouteGuard - Single source of truth for route access control
 * 
 * Combines authentication, role, tier, and beta checks in one component.
 * Uses React Query caching for roles (5-min staleTime via useUserRole).
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useSubscription, type SubscriptionTier } from '@/contexts/SubscriptionContext';
import { useBetaAccess } from '@/hooks/useBeta';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { toast } from 'sonner';
import { getTierDisplayName } from '@/lib/featureTiers';
import type { PlatformRole } from '@/lib/rbac/roles';

interface RouteGuardProps {
  children: ReactNode;
  /** Require authentication (default: true) */
  requireAuth?: boolean;
  /** Allowed platform roles (empty = any authenticated user) */
  allowedRoles?: PlatformRole[];
  /** Required subscription tier */
  requiredTier?: SubscriptionTier;
  /** Feature name for tier upgrade context */
  feature?: string;
  /** Require beta access */
  requireBeta?: boolean;
  /** Fallback component instead of redirect */
  fallback?: ReactNode;
  /** Redirect path for unauthorized access (default: /) */
  redirectTo?: string;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

export function RouteGuard({
  children,
  requireAuth = true,
  allowedRoles,
  requiredTier,
  feature,
  requireBeta = false,
  fallback,
  redirectTo = '/',
  loadingComponent,
}: RouteGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isReseller, isVerifier, isOperator, isConsultant, loading: roleLoading } = useUserRole();
  const { loading: tierLoading, hasAccess: hasTierAccess } = useSubscription();
  const { isLoading: betaLoading, hasBetaAccess } = useBetaAccess();
  const location = useLocation();
  const [hasShownToast, setHasShownToast] = useState(false);

  // Determine loading state based on required checks
  const isLoading = authLoading || 
    (requireAuth && (allowedRoles?.length ? roleLoading : false)) ||
    (requiredTier && tierLoading) ||
    (requireBeta && betaLoading);

  // Check role access
  const hasRoleAccess = !allowedRoles?.length || 
    (allowedRoles.includes('admin') && isAdmin) ||
    (allowedRoles.includes('reseller') && isReseller) ||
    (allowedRoles.includes('verifier') && isVerifier) ||
    (allowedRoles.includes('operator') && isOperator) ||
    (allowedRoles.includes('consultant') && isConsultant) ||
    (allowedRoles.includes('user') && !!user);

  // Show unauthorized toast once
  useEffect(() => {
    if (!isLoading && user && !hasRoleAccess && !hasShownToast && allowedRoles?.length) {
      const roleLabels = allowedRoles.join(' of ');
      toast.error('Geen toegang', {
        description: `Deze pagina is alleen toegankelijk voor ${roleLabels}.`,
        duration: 5000,
      });
      setHasShownToast(true);
    }
  }, [isLoading, user, hasRoleAccess, hasShownToast, allowedRoles]);

  // Loading state
  if (isLoading) {
    return loadingComponent ?? (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  // Auth check
  if (requireAuth && !user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Role check
  if (allowedRoles?.length && !hasRoleAccess) {
    if (fallback) return <>{fallback}</>;
    return <Navigate to={redirectTo} replace />;
  }

  // Tier check
  if (requiredTier && !hasTierAccess(requiredTier)) {
    toast.error(`${getTierDisplayName(requiredTier)} activatie vereist`, {
      description: 'Neem contact op om toegang tot deze functie te bespreken.',
    });
    
    const params = new URLSearchParams();
    params.set('context', 'activation');
    if (feature) params.set('feature', feature);
    if (requiredTier) params.set('tier', requiredTier);
    
    return <Navigate to={`/contact?${params.toString()}`} replace />;
  }

  // Beta check (admins bypass)
  if (requireBeta && !isAdmin && !hasBetaAccess) {
    return <Navigate to="/waitlist" replace />;
  }

  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPERS
// ═══════════════════════════════════════════════════════════════

/** Admin-only access */
export function AdminGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin']}>{children}</RouteGuard>;
}

/** Admin + Verifier access */
export function VerifierGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin', 'verifier']}>{children}</RouteGuard>;
}

/** Admin + Reseller access */
export function ResellerGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin', 'reseller']}>{children}</RouteGuard>;
}

/** Admin + Reseller access (alias for accounts routes) */
export function AccountsGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin', 'reseller']}>{children}</RouteGuard>;
}

/** Beta access required */
export function BetaGuard({ 
  children, 
  fallbackPath = '/waitlist' 
}: { 
  children: ReactNode; 
  fallbackPath?: string;
}) {
  return (
    <RouteGuard requireBeta redirectTo={fallbackPath}>
      {children}
    </RouteGuard>
  );
}

/** Operator access (admin + operator) */
export function OperatorGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin', 'operator']}>{children}</RouteGuard>;
}

/** Consultant access (admin + consultant) */
export function ConsultantGuard({ children }: { children: ReactNode }) {
  return <RouteGuard allowedRoles={['admin', 'consultant']}>{children}</RouteGuard>;
}
